import React, { useEffect } from 'react';
import { X, Copy, Check, Download } from 'lucide-react';
import { usePromptStore } from '../../store/promptStore';
import EmailCaptureModal from '../EmailCaptureModal';
import { extractYouTubeVideoId } from '../YouTubeEmbed';
import MDEditor from '@uiw/react-md-editor';
import YouTubeEmbed from '../YouTubeEmbed';

interface PromptPreviewModalProps {
  promptId: string;
  onClose: () => void;
}

export default function PromptPreviewModal({ promptId, onClose }: PromptPreviewModalProps) {
  const { selectedPrompt, loading, fetchPromptById } = usePromptStore();
  const [copied, setCopied] = React.useState(false);
  const [showDownloadModal, setShowDownloadModal] = React.useState(false);

  useEffect(() => {
    fetchPromptById(promptId);
  }, [promptId, fetchPromptById]);

  const handleCopy = () => {
    if (selectedPrompt) {
      navigator.clipboard.writeText(selectedPrompt.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    if (selectedPrompt?.download_url) {
      setShowDownloadModal(true);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedPrompt?.title}</h2>
              <p className="text-gray-600 mt-1">{selectedPrompt?.description}</p>
              {selectedPrompt?.tags && selectedPrompt.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {selectedPrompt.tags.map((tag) => (
                    <span key={tag.id} className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {selectedPrompt?.youtube_video_id && (
            <div className="mb-6 aspect-video w-full rounded-lg overflow-hidden">
              <YouTubeEmbed 
                videoId={selectedPrompt.youtube_video_id} 
                title={selectedPrompt.title}
              />
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F98B3D]"></div>
            </div>
          ) : (
            <div data-color-mode="light" className="markdown-editor-wrapper">
              <MDEditor.Markdown 
                source={selectedPrompt?.content || ''} 
                style={{ padding: '20px', fontSize: '16px', lineHeight: '1.6', marginBottom: '20px' }}
              />
              
              {selectedPrompt?.youtube_video_id && (
                <div className="mt-6 mb-6">
                  {/* YouTube video is already shown at the top */}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <div className="flex space-x-3">
            {selectedPrompt?.download_url && (
              <a
                href="#"
                onClick={handleDownload}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
              >
                <Download className="h-5 w-5 mr-2" />
                {selectedPrompt.download_button_text || 'Download Resource'}
              </a>
            )}
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D]"
            >
              {copied ? (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-5 w-5 mr-2" />
                  Copy Prompt
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Email Capture Modal for Downloads */}
      {selectedPrompt && showDownloadModal && (
        <EmailCaptureModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          downloadUrl={selectedPrompt.download_url || ''}
          promptId={selectedPrompt.id}
          source={`Prompt Admin Preview Download: ${selectedPrompt.title}`}
          title="Download Resource"
          description="Please enter your name and email to access this resource. We respect your privacy and will never share your information."
        />
      )}
    </div>
  );
}